<template>
    <div class="page-wrapper">
        <AppHeader />

        <div class="contact-view">
            <div class="contact-container">
                <h1 class="contact-title">{{ $t('contact.title') }}</h1>
                <template v-if="carImage">
                    <p class="contact-subtitle">
                        {{ $t('contact.about_car', { make: carMake, model: carModel, year: carYear }) }}
                    </p>
                </template>
                <template v-else>
                    <p class="contact-subtitle">
                        {{ $t('contact.subtitle') }}
                    </p>
                </template>
                
                <div v-if="success" class="success-message">
                    <div class="message-icon">✓</div>
                    <div class="message-text">
                        <h3>{{ $t('contact.success_title') }}</h3>
                        <p>{{ $t('contact.success_message') }}</p>
                    </div>
                </div>
                
                <div v-if="error" class="error-message">
                    <div class="message-icon">!</div>
                    <div class="message-text">
                        <h3>{{ $t('contact.error_title') }}</h3>
                        <p>{{ error }}</p>
                    </div>
                </div>
                
                <div class="contact-content">
                    <div class="contact-form-wrapper">
                        <ContactForm 
                          v-if="!success" 
                          @submit="handleSubmit" 
                          :loading="loading" 
                          :carDetails="carDetails" 
                        />	   
		</div>
                    <div class="contact-image-wrapper" v-if="carImage && !success">
                        <img :src="carImage" :alt="`${carMake} ${carModel}`" class="contact-image">
                        <div class="car-info-overlay" v-if="carMake && carModel">
                            <h3>{{ carMake }} {{ carModel }}</h3>
                            <p>{{ carYear }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="map-container" v-if="!success">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2510.206064614611!2d4.29419117686092!3d51.01349134666431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c3eb94c9e40c59%3A0x6dbf2d40e925c754!2sBASS%20MOTORS!5e0!3m2!1sfr!2sbe!4v1746295626713!5m2!1sfr!2sbe" 
            width="100%" 
            height="400" 
            style="border:0;" 
            allowfullscreen="" 
            loading="eager" 
            referrerpolicy="no-referrer-when-downgrade">
            </iframe>
        </div>
        <AppFooter />
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue'
    import axios from 'axios'
    import { useI18n } from 'vue-i18n'
    import AppHeader from '@/components/AppHeader.vue'
    import AppFooter from '@/components/AppFooter.vue'
    import ContactForm from '@/components/ContactForm.vue'
    import { useRoute } from 'vue-router'
    

    const { t } = useI18n()
    const loading = ref(false)
    const error = ref(null)
    const success = ref(false)

    const route = useRoute()

    const carImage = ref(route.query.carImage || null)
    const carMake = ref(route.query.carMake || null)
    const carModel = ref(route.query.carModel || null)
    const carYear = ref(route.query.carYear || null)

    const carDetails = computed(() => {
        return carMake.value ? {
            make: carMake.value,
            model: carModel.value,
            year: carYear.value
        } : null
    })

    const handleSubmit = async (formData) => {
        try {
            loading.value = true
            error.value = null
            success.value = false
            
            const response = await axios.post('/api/contacts', formData)
            
            if (response.data.status === "email_sent") {
                success.value = true
            } else {
                error.value = response.data.message || t('contact.error_message')
            }
        } catch (err) {
            error.value = err.response?.data?.message || 
                         err.response?.data?.detail || 
                         err.message || 
                         t('contact.error_message')
        } finally {
            loading.value = false
        }
    }
</script>

<style scoped>
    .success-message, .error-message {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        padding: 1.5rem;
        border-radius: 8px;
        margin-bottom: 2rem;
    }
    
    .success-message {
        background-color: #f0fdf4;
        border: 1px solid #bbf7d0;
        color: #166534;
    }
    
    .error-message {
        background-color: #fef2f2;
        border: 1px solid #fecaca;
        color: #991b1b;
    }
    
    .message-icon {
        font-size: 1.5rem;
        font-weight: bold;
        margin-top: 0.2rem;
    }
    
    .message-text h3 {
        margin: 0 0 0.5rem 0;
        font-size: 1.2rem;
    }
    
    .message-text p {
        margin: 0;
        line-height: 1.5;
    }

    .page-wrapper {
        display: flex;
        flex-direction: column;
        min-height: 100vh;
    }

    .contact-view {
        background-color: #f8f9fa;
        flex: 1;
        padding: 12rem 1rem;
        display: flex;
        justify-content: center;
    }

    .contact-container {
        max-width: 1200px;
        width: 100%;
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        padding: 2.5rem;
        margin: 2rem 0;
    }

    .contact-title {
        font-size: 2.5rem;
        font-weight: 700;
        color: #2c3e50;
        margin-bottom: 1rem;
        text-align: center;
    }

    .contact-subtitle {
        font-size: 1.1rem;
        color: #666;
        margin-bottom: 2.5rem;
        text-align: center;
        line-height: 1.6;
    }

    .contact-content {
        display: flex;
        gap: 2rem;
        margin-top: 2rem;
    }

    .contact-form-wrapper {
        flex: 1;
    }

    .contact-image-wrapper {
        flex: 1;
        display: flex;
        align-items: flex-start;
        justify-content: center;
    }

    .contact-image {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .contact-image-wrapper {
        flex: 1;
        position: relative;
    }

    .contact-image {
        width: 100%;
        height: auto;
        object-fit: cover;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .car-info-overlay {
        position: absolute;
        bottom: 125px;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        padding: 1rem;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    .car-info-overlay h3 {
        margin: 0;
        font-size: 1.2rem;
    }

    .car-info-overlay p {
        margin: 0.5rem 0 0;
        font-size: 1rem;
    }

    @media (max-width: 768px) {
        .contact-container {
            padding: 1.5rem;
        }
        
        .contact-title {
            font-size: 2rem;
        }
        
        .contact-subtitle {
            font-size: 1rem;
        }

        .success-message, .error-message {
            flex-direction: column;
            gap: 0.5rem;
        }

        .contact-content {
            flex-direction: column;
        }
        
        .contact-image-wrapper {
            order: -1;
            margin-bottom: 2rem;
        }
    }
</style>
